/**
 * Banker Inbox — lists loan cases routed to this banker.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import bankerApi from '../../lib/bankerApi';
import { Input } from '../../components/ui/input';
import { Search, Inbox, Handshake } from 'lucide-react';

// V4.25.11 — Semantic, high-contrast status pills (light colored bg + dark
// text + subtle ring). Replaces the old grey-bg/white-text pills that were
// unreadable for any status missing from the map.
const STATUS_META = {
  // Needs banker action — blue
  waiting_bank:       { label: 'Waiting Bank',      cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' },
  standard:           { label: 'Standard',          cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' },
  ready_pick_banker:  { label: 'Ready Pick Bank',   cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' },
  // In review — amber
  in_review:          { label: 'In Review',         cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  under_review:       { label: 'Under Review',      cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  waiting_review:     { label: 'Under Review',      cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  reviewing:          { label: 'Reviewing',         cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  submitted:          { label: 'Submitted',         cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  // Special handling — fuchsia
  special_handling:   { label: 'Special Handling',  cls: 'bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-200' },
  // Outsource flow — orange
  outsource:          { label: 'Outsource',         cls: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' },
  waiting_outsource:  { label: 'Waiting Outsource', cls: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' },
  bankers_available:  { label: 'Available Banker',  cls: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' },
  waiting_agent_pick: { label: 'Waiting Agent Pick',cls: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' },
  // Progressing — indigo / violet
  offer_accepted:     { label: 'Offer Accepted',    cls: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' },
  lo_meetup:          { label: 'LO Meetup',         cls: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' },
  lo_signed:          { label: 'LO Signed',         cls: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200' },
  // Success — emerald
  approved:           { label: 'Approved',          cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  drawdown:           { label: 'Drawn',             cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  // Terminal — rose / slate
  rejected:           { label: 'Rejected',          cls: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' },
  withdrawn:          { label: 'Withdrawn',         cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200' },
  closed:             { label: 'Closed',            cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200' },
};
const DEFAULT_PILL = 'bg-slate-100 text-slate-700 ring-1 ring-slate-200';
const pillClass = (s) => STATUS_META[s]?.cls || DEFAULT_PILL;
const pillLabel = (s) => STATUS_META[s]?.label
  || (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${pillClass(status)}`}>
    {pillLabel(status)}
  </span>
);

const fmtAmount = (v) => (v ? `RM ${Number(v).toLocaleString('en-MY')}` : '—');

export default function BankerInbox() {
  const [items, setItems] = useState([]);
  const [outsourceItems, setOutsourceItems] = useState([]);
  const [bankerId, setBankerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [inbox, outsource] = await Promise.all([
          bankerApi.get('/banker/cases'),
          bankerApi.get('/banker/outsource-cases').catch(() => ({ data: { items: [] } })),
        ]);
        setBankerId(inbox.data.banker_id || null);
        setItems(inbox.data.items || []);
        setOutsourceItems(outsource.data.items || []);
      } catch (e) { toast.error('Failed to load inbox'); }
      finally { setLoading(false); }
    })();
  }, []);

  // V4.25.11 — Loan Amount now falls back to THIS banker's own offer amount
  // when the case has no captured `loan_amount_requested` (which is common).
  const standardAmount = (c) => {
    if (c.loan_amount_requested) return c.loan_amount_requested;
    const mine = (c.offers || []).find((o) => o.banker_id === bankerId && (o.loan_amount || 0) > 0);
    return mine ? mine.loan_amount : null;
  };

  const filtered = items.filter(c =>
    !q ||
    (c.case_ref || '').toLowerCase().includes(q.toLowerCase()) ||
    (c.customer_name || '').toLowerCase().includes(q.toLowerCase()) ||
    (c.property_name || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="banker-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Inbox className="w-5 h-5" /> Inbox <span className="text-sm font-normal text-gray-500">({filtered.length})</span>
        </h1>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search case / customer / property"
            className="pl-8 w-72"
            data-testid="banker-inbox-search"
          />
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left p-2">Case Ref</th>
              <th className="text-left p-2">Customer</th>
              <th className="text-left p-2">Property</th>
              <th className="text-right p-2">Loan Amount</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-gray-400">
                No cases routed to you yet. Admin will fan-out new applications shortly.
              </td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`banker-case-row-${c.id}`}>
                <td className="p-2 font-mono text-xs">{c.case_ref}</td>
                <td className="p-2">
                  {c.customer_name || '—'}
                  <span className="block text-xs text-gray-400">{c.customer_ic || c.customer_phone || ''}</span>
                </td>
                <td className="p-2 text-xs">{c.property_name || '—'}</td>
                <td className="p-2 text-right text-xs">
                  {fmtAmount(standardAmount(c))}
                </td>
                <td className="p-2"><StatusPill status={c.status} /></td>
                <td className="p-2 text-right">
                  <Link
                    to={`/banker/cases/${c.id}`}
                    className="text-indigo-600 hover:underline text-sm"
                    data-testid={`banker-open-case-${c.id}`}
                  >Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* V4.24.51 — Outsource-referred cases */}
      <div className="pt-2">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-2">
          <Handshake className="w-5 h-5 text-amber-600" />
          Outsource-referred cases
          <span className="text-sm font-normal text-gray-500">({outsourceItems.length})</span>
        </h2>
        <p className="text-xs text-gray-500 mb-2">
          Loan cases that an outsource partner has fanned out to you. Click <em>Open</em> to submit your Approval Amount + Rate.
        </p>
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left p-2">Case Ref</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Property</th>
                <th className="text-left p-2">Referred by</th>
                <th className="text-right p-2">Loan Amount</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {outsourceItems.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-gray-400 text-xs">
                  No outsource-referred cases yet.
                </td></tr>
              )}
              {outsourceItems.map(c => (
                <tr key={c.assignment_id} className="border-t hover:bg-slate-50" data-testid={`banker-outsource-row-${c.assignment_id}`}>
                  <td className="p-2 font-mono text-xs">{c.case_ref || c.case_id?.slice(0, 8)}</td>
                  <td className="p-2">
                    {c.customer_name || '—'}
                    <span className="block text-xs text-gray-400">{c.customer_ic || c.customer_phone || ''}</span>
                  </td>
                  <td className="p-2 text-xs">{c.property_name || '—'}{c.unit_ref ? ` · #${c.unit_ref}` : ''}</td>
                  <td className="p-2 text-xs text-amber-700">{c.outsource_partner_name || '—'}</td>
                  <td className="p-2 text-right text-xs">
                    {fmtAmount(c.loan_amount || c.approval_amount || c.loan_amount_requested)}
                  </td>
                  <td className="p-2">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="p-2 text-right">
                    <Link
                      to={`/banker/outsource-cases/${c.assignment_id}`}
                      className="text-amber-700 hover:underline text-sm"
                      data-testid={`banker-open-outsource-${c.assignment_id}`}
                    >Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
